import { isDefined } from "@clipboard-health/util-ts";
import { useKnockTokenContext } from "@src/appV2/Knock/context";
import { updateKnockMessageStatus } from "@src/appV2/Knock/push/api";
import {
  APP_V2_APP_EVENTS,
  APP_V2_USER_EVENTS,
  logError,
  logEvent,
} from "@src/appV2/lib/analytics";
import { showInfoToast } from "@src/appV2/lib/Notifications";
import { parseChannelUrlForList } from "@src/appV2/redesign/Chat/page/channelList/channelListUtils";
import { CHAT_PAGE_PATH } from "@src/appV2/redesign/Chat/page/paths";
import { useIsChatRedesignEnabled } from "@src/appV2/redesign/hooks/useIsChatRedesignEnabled";
import {
  type PushNotificationActionPerformed,
  type PushNotificationSchema,
} from "@src/mobile/utils/pushNotifications.adapter";
import { useCallback, useEffect, useRef } from "react";
import { useHistory } from "react-router-dom";

import { mapLegacyRoute } from "../legacyRouteMapper";
import { clearNotificationTapHandler, setNotificationTapHandler } from "../notificationTapQueue";
import {
  type NotificationData,
  notificationDataSchema,
  UNCONSUMED_NOTIFICATION_LINK_KEY,
} from "../types";
import { isNotificationExpired } from "../utils/isNotificationExpired";
import {
  appendPendingKnockEngagement,
  clearPendingKnockEngagement,
  readPendingKnockEngagement,
} from "../utils/pendingKnockEngagement";

const CALENDAR_PATH = "/calendar";

function parseNotificationData(data: Record<string, unknown>): NotificationData {
  const result = notificationDataSchema.safeParse(data);
  if (result.success) {
    return result.data;
  }
  return {};
}

interface ChatRouteOptions {
  isChatRedesignEnabled: boolean;
}

function resolveChannelUrlToPath(channelUrl: string, options: ChatRouteOptions): string {
  const { isChatRedesignEnabled } = options;
  const parsed = parseChannelUrlForList(channelUrl);
  if (!isDefined(parsed)) {
    return isChatRedesignEnabled ? CHAT_PAGE_PATH : "/facility/chat";
  }

  const { workplaceId, workerId, placementId } = parsed;
  const chatPrefix = isChatRedesignEnabled ? "/chat" : "/facility/chat";
  if (isDefined(placementId)) {
    return `${chatPrefix}/${workplaceId}/${workerId}/${placementId}`;
  }
  return `${chatPrefix}/${workplaceId}/${workerId}`;
}

function resolveNavigationPath(data: NotificationData, options: ChatRouteOptions): string {
  // Priority 1: explicit link field
  if (isDefined(data.link) && data.link.length > 0) {
    // Try legacy route mapping first
    const legacyMapped = mapLegacyRoute(data.link, options);
    if (isDefined(legacyMapped)) {
      return legacyMapped;
    }

    // Let the router's catch-all redirect handle unknown routes
    return data.link;
  }

  // Priority 2: channel_url for chat notifications
  if (isDefined(data.channel_url) && data.channel_url.length > 0) {
    return resolveChannelUrlToPath(data.channel_url, options);
  }

  // Fallback: calendar home
  return CALENDAR_PATH;
}

interface UsePushNotificationHandlersParams {
  workplaceUserId: string;
}

/**
 * Hook that wires up push notification handling:
 * - Tap routing (cold-start + warm-start) via the module-scoped tap queue
 * - Foreground notification display via in-app toasts
 * - TTL expiry checking
 * - sessionStorage-backed unconsumed link for auth race condition
 * - Analytics logging
 *
 * Must be mounted inside the authenticated app tree.
 */
export function usePushNotificationHandlers(params: UsePushNotificationHandlersParams): void {
  const history = useHistory();
  const { workplaceUserId } = params;
  const isAuthenticated = isDefined(workplaceUserId) && workplaceUserId.length > 0;
  const isChatRedesignEnabled = useIsChatRedesignEnabled();
  const hasReplayedUnconsumedLink = useRef(false);
  // KnockTokenProvider only mounts for non-admin workplace sessions. For
  // admin / pre-auth, useKnockTokenContext returns constate's empty sentinel
  // and `knockToken` resolves to undefined. The cold-start tap dispatches
  // synchronously inside the handler-registration effect, often before
  // useGetCurrentUser has resolved — so taps with `knock_message_id` are
  // queued to sessionStorage and drained by the effect below once the JWT
  // is available.
  const { knockToken } = useKnockTokenContext();
  // Latest-value ref: keeps the receive/actionPerformed handlers' identity
  // stable across JWT refreshes, so the Capacitor push listeners aren't torn
  // down and re-registered every time the token rotates.
  const knockTokenRef = useRef(knockToken);
  knockTokenRef.current = knockToken;

  const navigateToPath = useCallback(
    (path: string) => {
      history.push(path);
    },
    [history]
  );

  const handleNotificationTap = useCallback(
    (action: PushNotificationActionPerformed) => {
      const data = parseNotificationData(action.notification.data);

      if (isDefined(data.knock_message_id)) {
        if (isDefined(knockTokenRef.current)) {
          void updateKnockMessageStatus({
            knockMessageId: data.knock_message_id,
            status: "interacted",
            userId: workplaceUserId,
            knockToken: knockTokenRef.current,
          });
        } else {
          appendPendingKnockEngagement({
            knockMessageId: data.knock_message_id,
            status: "interacted",
          });
        }
      }

      logEvent(APP_V2_USER_EVENTS.PUSH_NOTIFICATION_OPENED, {
        notification_type: data.type,
        has_link: isDefined(data.link) && data.link.length > 0,
        has_channel_url: isDefined(data.channel_url) && data.channel_url.length > 0,
        messageId: data.messageId,
        method: data.method,
      });

      // Check TTL expiry
      if (isNotificationExpired(data)) {
        if (isAuthenticated) {
          showInfoToast("This notification has expired");
          navigateToPath(CALENDAR_PATH);
        }
        return;
      }

      const targetPath = resolveNavigationPath(data, { isChatRedesignEnabled });

      if (!isAuthenticated) {
        // Store for replay after auth completes
        try {
          sessionStorage.setItem(UNCONSUMED_NOTIFICATION_LINK_KEY, targetPath);
        } catch {
          // sessionStorage may be unavailable in some contexts
        }
        return;
      }

      navigateToPath(targetPath);
    },
    [isAuthenticated, isChatRedesignEnabled, navigateToPath, workplaceUserId]
  );

  const handleForegroundNotification = useCallback(
    (notification: PushNotificationSchema) => {
      const data = parseNotificationData(notification.data);

      if (isDefined(knockTokenRef.current) && isDefined(data.knock_message_id)) {
        void updateKnockMessageStatus({
          knockMessageId: data.knock_message_id,
          status: "seen",
          userId: workplaceUserId,
          knockToken: knockTokenRef.current,
        });
      }

      logEvent(APP_V2_USER_EVENTS.PUSH_NOTIFICATION_RECEIVED, {
        notification_type: data.type,
        messageId: data.messageId,
        method: data.method,
      });

      if (isNotificationExpired(data)) {
        return;
      }

      const title = notification.title ?? "New notification";
      showInfoToast(title);
    },
    [workplaceUserId]
  );

  // Reset replay guard when user logs out so replay can occur on next auth
  useEffect(() => {
    if (!isAuthenticated) {
      hasReplayedUnconsumedLink.current = false;
    }
  }, [isAuthenticated]);

  // Replay unconsumed notification link after auth
  useEffect(() => {
    if (!isAuthenticated || hasReplayedUnconsumedLink.current) {
      return;
    }

    hasReplayedUnconsumedLink.current = true;

    try {
      const unconsumedLink = sessionStorage.getItem(UNCONSUMED_NOTIFICATION_LINK_KEY);
      if (isDefined(unconsumedLink) && unconsumedLink.length > 0) {
        sessionStorage.removeItem(UNCONSUMED_NOTIFICATION_LINK_KEY);
        navigateToPath(unconsumedLink);
      }
    } catch {
      // sessionStorage may be unavailable
    }
  }, [isAuthenticated, navigateToPath]);

  // Drain queued Knock engagement events once the JWT is available.
  useEffect(() => {
    if (!isAuthenticated || !isDefined(knockToken)) {
      return;
    }

    const pending = readPendingKnockEngagement();
    if (pending.length === 0) {
      return;
    }

    clearPendingKnockEngagement();

    for (const entry of pending) {
      void updateKnockMessageStatus({
        knockMessageId: entry.knockMessageId,
        status: entry.status,
        userId: workplaceUserId,
        knockToken,
      });
    }
  }, [isAuthenticated, knockToken, workplaceUserId]);

  // Wire up the module-scoped tap queue
  useEffect(() => {
    setNotificationTapHandler(handleNotificationTap);

    return () => {
      clearNotificationTapHandler();
    };
  }, [handleNotificationTap]);

  // Wire up foreground notification listener
  useEffect(() => {
    let isCancelled = false;
    let listenerHandle: { remove: () => Promise<void> } | undefined;

    void (async () => {
      const { addPushNotificationReceivedListener } =
        await import("@src/mobile/utils/pushNotifications.adapter");

      const handle = await addPushNotificationReceivedListener((notification) => {
        if (!isCancelled) {
          handleForegroundNotification(notification);
        }
      });

      if (isCancelled) {
        void handle.remove();
      } else {
        listenerHandle = handle;
      }
    })().catch((error: unknown) => {
      logError(APP_V2_APP_EVENTS.PUSH_NOTIFICATION_LISTENER_SETUP_FAILURE, { error });
    });

    return () => {
      isCancelled = true;
      if (isDefined(listenerHandle)) {
        void listenerHandle.remove();
      }
    };
  }, [handleForegroundNotification]);
}
